import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Email,
  Refresh,
  PlayArrow,
  Settings,
  Info,
  CheckCircle,
  Error,
  Schedule,
  Timeline,
  Storage,
  Computer,
  Send,
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { emailAPI } from '../services/api'
import AdminPageWrapper from '../components/AdminPageWrapper'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorAlert from '../components/ErrorAlert'

const AdminEmailManagementPage = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [emailStatus, setEmailStatus] = useState(null)
  const [pollingStatus, setPollingStatus] = useState(null)
  const [listeners, setListeners] = useState([])
  const [triggerLoading, setTriggerLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(false)
  const [testLoading, setTestLoading] = useState(false)
  const [fetchDialogOpen, setFetchDialogOpen] = useState(false)
  const [fetchOptions, setFetchOptions] = useState({
    folder: 'inbox',
    max_emails: 50,
    unread_only: true
  })
  const refreshInterval = useRef(null)
  const countdownInterval = useRef(null)
  const [countdown, setCountdown] = useState(null)

  const loadData = async () => {
    try {
      setError(null)
      const [statusData, pollingData, listenersData] = await Promise.all([
        emailAPI.getStatus(),
        emailAPI.getPollingStatus().catch(() => null), // Might not be available
        emailAPI.getListeners().catch(() => ({ listeners: [] }))
      ])
      
      setEmailStatus(statusData)
      setPollingStatus(pollingData)
      setListeners(listenersData.listeners || [])
    } catch (err) {
      console.error('Failed to load email management data:', err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    
    // Set up auto-refresh every 10 seconds for more frequent polling status updates
    refreshInterval.current = setInterval(loadData, 10000)
    
    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current)
      }
    }
  }, [])

  const handleTriggerPoll = async () => {
    setTriggerLoading(true)
    try {
      await emailAPI.triggerPoll()
      await loadData() // Refresh data after trigger
    } catch (err) {
      console.error('Failed to trigger poll:', err)
      setError(err)
    } finally {
      setTriggerLoading(false)
    }
  }

  const handleFetchEmails = async () => {
    setFetchLoading(true)
    try {
      await emailAPI.fetchEmails(fetchOptions)
      setFetchDialogOpen(false)
      await loadData() // Refresh data after fetch
    } catch (err) {
      console.error('Failed to fetch emails:', err)
      setError(err)
    } finally {
      setFetchLoading(false)
    }
  }

  const handleTestConnection = async () => {
    setTestLoading(true)
    try {
      const result = await emailAPI.testConnection()
      // TODO: Show success/failure notification
      console.log('Connection test result:', result)
    } catch (err) {
      console.error('Connection test failed:', err)
      setError(err)
    } finally {
      setTestLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    try {
      const date = new Date(dateString)
      const relativeTime = formatDistanceToNow(date, { addSuffix: true })
      const actualDate = date.toLocaleDateString()
      return `${relativeTime} (${actualDate})`
    } catch {
      return 'Invalid date'
    }
  }

  const getStatusChip = (available, running = null) => {
    if (running === false) {
      return <Chip icon={<Error />} label="Stopped" color="warning" size="small" />
    }
    if (available && (running === true || running === null)) {
      return <Chip icon={<CheckCircle />} label="Active" color="success" size="small" />
    }
    return <Chip icon={<Error />} label="Unavailable" color="error" size="small" />
  }

  const calculateNextPoll = (lastPollTime, intervalSeconds) => {
    if (!lastPollTime || !intervalSeconds) return 'Unknown'
    try {
      const lastPoll = new Date(lastPollTime)
      const nextPoll = new Date(lastPoll.getTime() + (intervalSeconds * 1000))
      const now = new Date()
      
      if (nextPoll <= now) {
        return 'Due now'
      }
      return formatDistanceToNow(nextPoll, { addSuffix: true })
    } catch {
      return 'Unknown'
    }
  }

  // Calculate countdown to next poll in seconds
  const calculateCountdownSeconds = (lastPollTime, intervalSeconds) => {
    if (!lastPollTime || !intervalSeconds) return null
    try {
      const lastPoll = new Date(lastPollTime)
      const nextPoll = new Date(lastPoll.getTime() + (intervalSeconds * 1000))
      const now = new Date()
      const secondsRemaining = Math.ceil((nextPoll - now) / 1000)
      return Math.max(0, secondsRemaining)
    } catch {
      return null
    }
  }

  // Format countdown display with precise seconds
  const formatCountdown = (seconds) => {
    if (seconds === null) return 'Unknown'
    if (seconds <= 0) return 'Due now'
    if (seconds < 60) return `${seconds} seconds`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    if (remainingSeconds === 0) return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    return `${minutes}m ${remainingSeconds}s`
  }

  // Update countdown every second
  useEffect(() => {
    const updateCountdown = () => {
      if (pollingStatus?.statistics?.last_poll_time && pollingStatus?.configuration?.interval_seconds) {
        const seconds = calculateCountdownSeconds(
          pollingStatus.statistics.last_poll_time,
          pollingStatus.configuration.interval_seconds
        )
        setCountdown(seconds)
      } else {
        setCountdown(null)
      }
    }

    // Update immediately
    updateCountdown()

    // Update every second
    countdownInterval.current = setInterval(updateCountdown, 1000)

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current)
      }
    }
  }, [pollingStatus?.statistics?.last_poll_time, pollingStatus?.configuration?.interval_seconds])

  if (loading) {
    return (
      <AdminPageWrapper title="Email Management">
        <LoadingSpinner message="Loading email management..." />
      </AdminPageWrapper>
    )
  }

  if (error) {
    return (
      <AdminPageWrapper title="Email Management">
        <ErrorAlert
          message="Failed to load email management data"
          error={error}
          onRetry={loadData}
        />
      </AdminPageWrapper>
    )
  }

  return (
    <AdminPageWrapper
      title="Email Management"
      subtitle="Monitor email service status, background polling, and email listeners."
    >
      {/* Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Email Service Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2">
                  Email Service Status
                </Typography>
                {getStatusChip(emailStatus?.available)}
              </Box>
              
              {emailStatus && (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Configuration
                      </Typography>
                      {Object.entries(emailStatus.configuration || {}).map(([key, value]) => (
                        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="body2">{key}:</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                    
                    {emailStatus.missing_variables?.length > 0 && (
                      <Grid item xs={12}>
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <Typography variant="subtitle2">Missing Configuration:</Typography>
                          {emailStatus.missing_variables.map(variable => (
                            <Typography key={variable} variant="body2" sx={{ fontFamily: 'monospace' }}>
                              • {variable}
                            </Typography>
                          ))}
                        </Alert>
                      </Grid>
                    )}
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Computer />}
                      onClick={handleTestConnection}
                      disabled={!emailStatus.available || testLoading}
                      size="small"
                    >
                      {testLoading ? <CircularProgress size={16} /> : 'Test Connection'}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Polling Service Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2">
                  Background Polling
                </Typography>
                {pollingStatus && getStatusChip(pollingStatus.email_service_available, pollingStatus.is_running)}
              </Box>
              
              {pollingStatus ? (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Enabled</Typography>
                      <Typography variant="body2">
                        {pollingStatus.configuration?.enabled ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Interval</Typography>
                      <Typography variant="body2">
                        {pollingStatus.configuration?.interval_seconds}s
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Max Emails</Typography>
                      <Typography variant="body2">
                        {pollingStatus.configuration?.max_emails}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Folder</Typography>
                      <Typography variant="body2">
                        {pollingStatus.configuration?.folder}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Last Poll</Typography>
                      <Typography variant="body2">
                        {formatDate(pollingStatus.statistics?.last_poll_time)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Next Poll</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 'medium', 
                          color: countdown <= 5 ? 'warning.main' : 'inherit',
                          ...(countdown <= 3 && countdown > 0 && {
                            '@keyframes pulse': {
                              '0%': { opacity: 1 },
                              '50%': { opacity: 0.6 },
                              '100%': { opacity: 1 }
                            },
                            animation: 'pulse 1s infinite'
                          })
                        }}
                      >
                        {formatCountdown(countdown)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Total Polls</Typography>
                      <Typography variant="body2">
                        {pollingStatus.statistics?.total_polls || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Emails Processed</Typography>
                      <Typography variant="body2">
                        {pollingStatus.statistics?.total_emails_processed || 0}
                      </Typography>
                    </Grid>
                  </Grid>

                  {pollingStatus.statistics?.last_error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Last Error:</Typography>
                      <Typography variant="body2">{pollingStatus.statistics.last_error}</Typography>
                      <Typography variant="caption">
                        {formatDate(pollingStatus.statistics.last_error_time)}
                      </Typography>
                    </Alert>
                  )}

                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrow />}
                      onClick={handleTriggerPoll}
                      disabled={!pollingStatus.email_service_available || triggerLoading}
                      size="small"
                    >
                      {triggerLoading ? <CircularProgress size={16} /> : 'Trigger Poll'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Send />}
                      onClick={() => setFetchDialogOpen(true)}
                      disabled={!emailStatus?.available}
                      size="small"
                    >
                      Manual Fetch
                    </Button>
                  </Box>
                </>
              ) : (
                <Alert severity="info">
                  Background polling service is not available
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Email Listeners */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Email Listeners ({listeners.length})
              </Typography>
              
              {listeners.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Rules</TableCell>
                        <TableCell>Patterns</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listeners.map((listener, index) => (
                        <TableRow key={index}>
                          <TableCell>{listener.name}</TableCell>
                          <TableCell>
                            <Chip label={listener.type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>{listener.rules?.length || 0}</TableCell>
                          <TableCell>
                            {listener.rules?.map((rule, ruleIndex) => (
                              <Box key={ruleIndex} sx={{ mb: 1 }}>
                                <Typography variant="caption" display="block">
                                  {rule.name} {rule.enabled ? '✓' : '✗'}
                                </Typography>
                                {Object.entries(rule.patterns || {}).map(([type, pattern]) => (
                                  <Typography key={type} variant="caption" sx={{ 
                                    fontFamily: 'monospace', 
                                    fontSize: '0.7rem',
                                    display: 'block',
                                    color: 'text.secondary'
                                  }}>
                                    {type}: {pattern}
                                  </Typography>
                                ))}
                              </Box>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No email listeners configured
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Manual Fetch Dialog */}
      <Dialog open={fetchDialogOpen} onClose={() => setFetchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manual Email Fetch</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Folder"
              value={fetchOptions.folder}
              onChange={(e) => setFetchOptions(prev => ({ ...prev, folder: e.target.value }))}
              sx={{ mb: 2 }}
              size="small"
            />
            <TextField
              fullWidth
              label="Max Emails"
              type="number"
              value={fetchOptions.max_emails}
              onChange={(e) => setFetchOptions(prev => ({ ...prev, max_emails: parseInt(e.target.value) }))}
              sx={{ mb: 2 }}
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={fetchOptions.unread_only}
                  onChange={(e) => setFetchOptions(prev => ({ ...prev, unread_only: e.target.checked }))}
                />
              }
              label="Unread only"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFetchDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleFetchEmails} 
            variant="contained"
            disabled={fetchLoading}
          >
            {fetchLoading ? <CircularProgress size={20} /> : 'Fetch Emails'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPageWrapper>
  )
}

export default AdminEmailManagementPage